export interface SizeClass {
  label: string;
  lo: number;
  hi: number;
}

export const SIZE_CLASSES: SizeClass[] = [
  { label: '0-1 Kg', lo: 0, hi: 1 },
  { label: '1-2 Kg', lo: 1, hi: 2 },
  { label: '2-3 Kg', lo: 2, hi: 3 },
  { label: '3-4 Kg', lo: 3, hi: 4 },
  { label: '4-5 Kg', lo: 4, hi: 5 },
  { label: '5-6 Kg', lo: 5, hi: 6 },
  { label: '6+ Kg', lo: 6, hi: Infinity },
];

export type SizeClassLabel = (typeof SIZE_CLASSES)[number]['label'];

export const DEFAULT_PRICES: Record<SizeClassLabel, number> = {
  '0-1 Kg': 15,
  '1-2 Kg': 25,
  '2-3 Kg': 30,
  '3-4 Kg': 45,
  '4-5 Kg': 63,
  '5-6 Kg': 65,
  '6+ Kg': 67,
};

export const DEFAULT_CV = 10;

const SQRT_2PI = Math.sqrt(2 * Math.PI);

function erf(x: number): number {
  const sign = Math.sign(x);
  const ax = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return sign * y;
}

export function normalCdf(z: number): number {
  if (z === Infinity) return 1;
  if (z === -Infinity) return 0;
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

export function normalPdf(z: number): number {
  if (!isFinite(z)) return 0;
  return Math.exp(-0.5 * z * z) / SQRT_2PI;
}

export function expectedRevenueByClassPerGroup(
  record: { guttedWeightEndOfWeek: number; fishEndWeek: number },
  cvPct: number,
  prices: Record<SizeClassLabel, number>
): Record<SizeClassLabel, number> {
  const meanKg = record.guttedWeightEndOfWeek / 1000;
  const result: Record<SizeClassLabel, number> = {} as Record<SizeClassLabel, number>;
  for (const c of SIZE_CLASSES) result[c.label] = 0;
  if (meanKg <= 0 || record.fishEndWeek <= 0) return result;

  const sigma = meanKg * (cvPct / 100);
  if (sigma <= 0) {
    const lab = SIZE_CLASSES.find((c) => meanKg >= c.lo && meanKg < c.hi)?.label ?? SIZE_CLASSES[SIZE_CLASSES.length - 1].label;
    result[lab] = meanKg * (prices[lab] ?? 0) * record.fishEndWeek;
    return result;
  }

  for (const c of SIZE_CLASSES) {
    const alpha = (c.lo - meanKg) / sigma;
    const beta = c.hi === Infinity ? Infinity : (c.hi - meanKg) / sigma;
    const p = normalCdf(beta) - normalCdf(alpha);
    if (p < 1e-9) continue;
    const phiAlpha = normalPdf(alpha);
    const phiBeta = c.hi === Infinity ? 0 : normalPdf(beta);
    const truncMean = meanKg + sigma * ((phiAlpha - phiBeta) / p);
    result[c.label] = p * (prices[c.label] ?? 0) * truncMean * record.fishEndWeek;
  }
  return result;
}
